export interface SearchSuggestion {
  original: string;
  corrected: string;
  confidence: number;
  type: 'typo' | 'synonym' | 'expansion';
}

// Common typos and their corrections
const TYPO_CORRECTIONS = {
  // Countries
  'spane': 'spanje',
  'spance': 'spanje', 
  'spannië': 'spanje',
  'italian': 'italië',
  'italie': 'italië',
  'griekenlan': 'griekenland',
  'grikenland': 'griekenland',
  'turkue': 'turkije',
  'turkye': 'turkije',
  'portugl': 'portugal',
  'egiptë': 'egypte',
  'egipte': 'egypte',
  'tailand': 'thailand',
  'thayland': 'thailand',
  'vietman': 'vietnam',
  'indonezie': 'indonesië',
  'malesië': 'maleisië',
  
  // Cities/Regions
  'costa brave': 'costa brava',
  'costa baba': 'costa brava',
  'llorit': 'lloret',
  'lloret del mar': 'lloret de mar',
  'tose de mar': 'tossa de mar',
  'krete': 'kreta',
  'creata': 'kreta',
  'santorini': 'santorini',
  'mykonys': 'mykonos',
  'rodos': 'rhodos',
  'rodhos': 'rhodos',
  'antalya': 'antalya',
  'antalja': 'antalya',
  'side': 'side',
  'belek': 'belek',
  'kemmer': 'kemer',
  'alanya': 'alanya',
  
  // Experience types
  'rondrei': 'rondreis',
  'ronreis': 'rondreis',
  'rondris': 'rondreis',
  'stadtstrip': 'stedentrip',
  'stedentryp': 'stedentrip',
  'citytryp': 'stedentrip',
  'safarri': 'safari',
  'safary': 'safari',
  'cruize': 'cruise',
  'cruse': 'cruise',
  'winterspoort': 'wintersport',
  'winterport': 'wintersport',
  'wellnes': 'wellness',
  'welness': 'wellness',
  
  // Facilities
  'zwembat': 'zwembad',
  'zwembaat': 'zwembad',
  'all inclussive': 'all inclusive',
  'al inclusive': 'all inclusive',
  'all inclusieve': 'all inclusive',
  'adults onluy': 'adults only',
  'adults onlu': 'adults only',
  'aduls only': 'adults only',
  
  // Common words
  'vakantye': 'vakantie',
  'vakanty': 'vakantie',
  'vakantis': 'vakantie',
  'rels': 'reis',
  'ryis': 'reis',
  'hotel': 'hotel',
  'hotle': 'hotel',
  'hotell': 'hotel'
};

// Synonyms for better search coverage
const SEARCH_SYNONYMS = {
  // Location synonyms
  'spanje': ['spain', 'españa', 'espagne'],
  'italië': ['italy', 'italia', 'italie'],
  'griekenland': ['greece', 'hellas', 'ellada'],
  'turkije': ['turkey', 'türkiye'],
  'frankrijk': ['france', 'francia'],
  'portugal': ['portugese', 'lusitanië'],
  'egypte': ['egypt', 'aegyptus'],
  'thailand': ['tailand', 'siam'],
  'vietnam': ['viet nam', 'indochina'],
  'indonesië': ['indonesia', 'dutch east indies'],
  
  // Experience synonyms
  'rondreis': ['roadtrip', 'rondtour', 'circuit', 'tour'],
  'stedentrip': ['citytrip', 'stadsreis', 'urban trip', 'city break'],
  'cruise': ['cruises', 'scheepsreis', 'boat trip'],
  'safari': ['wildlife tour', 'natuur safari', 'game drive'],
  'wintersport': ['ski', 'snowboard', 'winter sports', 'snow holiday'],
  'wellness': ['spa', 'ontspanning', 'relaxation', 'health'],
  'strand': ['beach', 'kust', 'seaside', 'waterfront'],
  'all inclusive': ['ai', 'alles inbegrepen', 'full board plus'],
  'adults only': ['18+', 'volwassenen', 'no kids', 'adult only'],
  
  // Accommodation synonyms
  'hotel': ['resort', 'accommodatie', 'verblijf'],
  'appartement': ['apartment', 'flat', 'studio'],
  'villa': ['house', 'huis', 'vakantievilla'],
  'camping': ['campsite', 'kamperen', 'caravan'],
  
  // Activity synonyms
  'zwemmen': ['swimming', 'baden', 'water sport'],
  'duiken': ['diving', 'snorkeling', 'underwater'],
  'wandelen': ['hiking', 'trekking', 'walking'],
  'fietsen': ['cycling', 'biking', 'bike tour'],
  'golf': ['golfing', 'golf course', 'golf resort'],
  
  // Weather synonyms
  'zon': ['sunny', 'sunshine', 'warm weather'],
  'warm': ['hot', 'tropical', 'summer'],
  'koud': ['cold', 'winter', 'snow'],
  'droog': ['dry', 'desert', 'arid']
};

export const correctTypos = (query: string): SearchSuggestion[] => {
  const suggestions: SearchSuggestion[] = [];
  const words = query.toLowerCase().split(/\s+/);
  
  words.forEach((word, index) => {
    if (TYPO_CORRECTIONS[word]) {
      suggestions.push({
        original: word,
        corrected: TYPO_CORRECTIONS[word],
        confidence: 0.9,
        type: 'typo'
      });
    }
  });
  
  return suggestions;
};

export const expandWithSynonyms = (query: string): string[] => {
  const expandedQueries: string[] = [query];
  const words = query.toLowerCase().split(/\s+/);
  
  words.forEach(word => {
    if (SEARCH_SYNONYMS[word]) {
      SEARCH_SYNONYMS[word].forEach(synonym => {
        const expandedQuery = query.toLowerCase().replace(word, synonym);
        if (!expandedQueries.includes(expandedQuery)) {
          expandedQueries.push(expandedQuery);
        }
      });
    }
  });
  
  return expandedQueries;
};

export const suggestQueryImprovements = (query: string): SearchSuggestion[] => {
  const suggestions: SearchSuggestion[] = [];
  const lowerQuery = query.toLowerCase();
  
  // Check for typos
  const typoSuggestions = correctTypos(query);
  suggestions.push(...typoSuggestions);
  
  // Suggest more specific queries
  if (lowerQuery === 'vakantie' || lowerQuery === 'reis') {
    suggestions.push({
      original: query,
      corrected: `${query} + bestemming (bijv. "vakantie Spanje")`,
      confidence: 0.8,
      type: 'expansion'
    });
  }
  
  // Suggest adding timeframe
  if (!lowerQuery.includes('januari') && !lowerQuery.includes('februari') && 
      !lowerQuery.includes('maart') && !lowerQuery.includes('zomer') &&
      (lowerQuery.includes('zon') || lowerQuery.includes('warm'))) {
    suggestions.push({
      original: query,
      corrected: `${query} + maand (bijv. "${query} juli")`,
      confidence: 0.7,
      type: 'expansion'
    });
  }
  
  // Suggest adding budget
  if (!lowerQuery.includes('€') && !lowerQuery.includes('budget') && 
      !lowerQuery.includes('max') && lowerQuery.length > 5) {
    suggestions.push({
      original: query,
      corrected: `${query} + budget (bijv. "${query} max €800")`,
      confidence: 0.6,
      type: 'expansion'
    });
  }
  
  return suggestions;
};

export const getSmartSearchQuery = (originalQuery: string): string => {
  let improvedQuery = originalQuery;
  
  // Apply typo corrections
  const typoSuggestions = correctTypos(originalQuery);
  typoSuggestions.forEach(suggestion => {
    if (suggestion.confidence > 0.8) {
      improvedQuery = improvedQuery.replace(
        new RegExp(suggestion.original, 'gi'), 
        suggestion.corrected
      );
    }
  });
  
  return improvedQuery;
};